/**
 * AuditTrailViewer — F-012 Phase 3 (IMPL-P3-007).
 *
 * Immutable, append-only timeline of fido changes for a business account. Each
 * entry shows: admin (id), timestamp, old→new fido in euro, motivazione and an
 * action badge (SET / GRACE / BLOCK / UNBLOCK). Most-recent entry first.
 *
 * Markup uses a semantic `<ol role="list">` + `<li>` per the codebase timeline
 * pattern; the action badge carries a text label + variant (never color alone).
 */
import { History } from 'lucide-react';
import type { FidoAuditEntryView } from '../lib/admin-client';
import { centsToEuro, formatDateTime } from '../lib/format';
import { ActionBadge } from './WalletBadges';

interface AuditTrailViewerProps {
  entries: FidoAuditEntryView[];
}

export function AuditTrailViewer({ entries }: AuditTrailViewerProps) {
  // Defensive: render most-recent first regardless of server order.
  const sorted = [...entries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  if (sorted.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        <History className="mx-auto mb-2 h-5 w-5" aria-hidden="true" />
        Nessuna modifica registrata.
      </div>
    );
  }

  return (
    <ol role="list" className="space-y-3">
      {sorted.map((entry) => (
        <li
          key={entry.id}
          data-action={entry.action}
          className="rounded-md border bg-card p-3 text-sm"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="font-medium tabular-nums">
                <span className="text-muted-foreground">{centsToEuro(entry.oldValueCents)}</span>
                <span aria-hidden="true"> → </span>
                <span className="sr-only"> diventa </span>
                {centsToEuro(entry.newValueCents)}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDateTime(entry.createdAt)} · {entry.adminId}
              </p>
            </div>
            <ActionBadge action={entry.action} />
          </div>
          {entry.motivazione ? (
            <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{entry.motivazione}</p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
